import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Form, Input, Switch, Button, Upload, notification } from "antd";
import { Pencil, Trash2 } from "lucide-react";
import { MenuOutlined } from "@ant-design/icons";
import { RxUpload } from "react-icons/rx";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  alignOptionLogos,
  joinChoiceOptionEditorLines,
  parseChoiceOptionEditorLines,
  resolveShowLogosFlag,
  splitChoiceOptionTokens,
} from "./constants";
import {
  ALLOWED_LOGO_EXTENSIONS,
  ALLOWED_LOGO_MIME_TYPES,
} from "../../../constants/organization";
import { getMimeFromExtension } from "../../../utils/getMimeFromExtension";

const lockVerticalDragStyle = (style) => {
  if (!style) return style;

  if (!style.transform) return style;

  let y = "0px";
  const transform = style.transform;
  const translate3d = transform.match(
    /translate3d\(\s*([^,]+),\s*([^,]+),\s*([^)]+)\)/,
  );
  const translate = transform.match(/translate\(\s*([^,]+),\s*([^)]+)\)/);
  const matrix = transform.match(/matrix\(\s*([^)]+)\)/);

  if (translate3d) {
    y = translate3d[2].trim();
  } else if (translate) {
    y = translate[2].trim();
  } else if (matrix) {
    const parts = matrix[1].split(",").map((part) => part.trim());
    y = `${parts[5] || 0}px`;
  }

  return {
    ...style,
    transform: `translate(0px, ${y})`,
  };
};

const validateLogoFile = (file) => {
  const inferredMime = file.type || getMimeFromExtension(file.name);
  const fileName = file.name?.toLowerCase() || "";
  const hasAllowedExtension = ALLOWED_LOGO_EXTENSIONS.some((ext) =>
    fileName.endsWith(ext),
  );
  const hasAllowedMime = ALLOWED_LOGO_MIME_TYPES.includes(inferredMime);

  if (!hasAllowedMime || !hasAllowedExtension) {
    notification.error({
      message: "Upload Error",
      description: "Only PNG, JPG/JPEG, and WEBP files are supported.",
    });
    return false;
  }

  if (file.size / 1024 / 1024 >= 2) {
    notification.error({
      message: "Upload Error",
      description: "Image must be smaller than 2MB.",
    });
    return false;
  }

  return true;
};

const ChoiceOptionsEditor = ({
  input,
  updateInput,
  mainForm,
  uploadFile,
  optionLabel,
  preventDragHandling,
}) => {
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const watchedShowLogos = Form.useWatch(`show_logos_${input.id}`, mainForm);
  const showLogos = Boolean(watchedShowLogos ?? resolveShowLogosFlag(input));

  useEffect(() => {
    if (watchedShowLogos != null) return;
    if (!resolveShowLogosFlag(input)) return;
    mainForm.setFieldValue(`show_logos_${input.id}`, true);
  }, [input, mainForm, watchedShowLogos]);

  const lines = parseChoiceOptionEditorLines(input.options);
  const displayLines = lines.length > 0 ? lines : [""];
  const logos = alignOptionLogos(input.option_logos, displayLines.length);

  const syncLines = (nextLines, nextLogos = logos) => {
    const effectiveLines = nextLines.length > 0 ? nextLines : [""];

    updateInput(input.id, {
      options: joinChoiceOptionEditorLines(effectiveLines),
      option_logos: alignOptionLogos(nextLogos, effectiveLines.length),
    });
  };

  const handleShowLogosChange = (checked) => {
    mainForm.setFieldValue(`show_logos_${input.id}`, checked);

    const currentLines = parseChoiceOptionEditorLines(input.options);
    const effectiveLines = currentLines.length > 0 ? currentLines : [""];
    const joinedOptions = joinChoiceOptionEditorLines(effectiveLines);

    if (!checked) {
      updateInput(input.id, {
        show_logos: false,
        options: joinedOptions,
        option_logos: [],
      });
      return;
    }

    updateInput(input.id, {
      show_logos: true,
      options: joinedOptions,
      option_logos: alignOptionLogos([], effectiveLines.length),
    });
  };

  const handleLineChange = (index, value) => {
    const nextLines = [...displayLines];
    nextLines[index] = value;

    if (
      value.includes(",") &&
      !value.includes("\n") &&
      splitChoiceOptionTokens(value).length > 1
    ) {
      const tokens = splitChoiceOptionTokens(value);
      nextLines.splice(index, 1, ...tokens);
      syncLines(nextLines);
      return;
    }

    syncLines(nextLines);
  };

  const handleAddLine = () => {
    const nextLines = [...displayLines, ""];
    const nextLogos = alignOptionLogos(logos, nextLines.length);
    syncLines(nextLines, nextLogos);
  };

  const handleRemoveLine = (index) => {
    if (displayLines.length <= 1) {
      syncLines([""], []);
      return;
    }

    const nextLines = displayLines.filter((_, idx) => idx !== index);
    const nextLogos = logos.filter((_, idx) => idx !== index);
    syncLines(nextLines, nextLogos);
  };

  const handleOptionsDragEnd = (result) => {
    if (!result.destination) return;

    if (result.destination.index === result.source.index) return;

    const nextLines = [...displayLines];
    const nextLogos = [...logos];
    const [movedLine] = nextLines.splice(result.source.index, 1);
    const [movedLogo] = nextLogos.splice(result.source.index, 1);
    nextLines.splice(result.destination.index, 0, movedLine);
    nextLogos.splice(result.destination.index, 0, movedLogo);
    syncLines(nextLines, nextLogos);
  };

  const uploadLogoForRow = async (file, rowIndex) => {
    if (!validateLogoFile(file)) {
      return;
    }

    try {
      setUploadingIndex(rowIndex);
      const inferredMime = file.type || getMimeFromExtension(file.name);
      const normalizedFile =
        !file.type && inferredMime
          ? new File([file], file.name, { type: inferredMime })
          : file;

      const data = await uploadFile({ file: normalizedFile }).unwrap();

      if (!data?.file) {
        throw new Error("Upload failed");
      }

      const nextLogos = alignOptionLogos(logos, displayLines.length);
      nextLogos[rowIndex] = data.file;
      updateInput(input.id, "option_logos", nextLogos);
    } catch {
      notification.error({
        message: "Upload Failed",
        description: "Failed to upload logo. Please try again.",
      });
    } finally {
      setUploadingIndex(null);
    }
  };

  const logoUploadProps = (rowIndex) => ({
    showUploadList: false,
    accept: ALLOWED_LOGO_EXTENSIONS.join(","),
    beforeUpload: (file) => {
      uploadLogoForRow(file, rowIndex);
      return false;
    },
  });

  const handleRemoveLogo = (index) => {
    const nextLogos = alignOptionLogos(logos, displayLines.length);
    nextLogos[index] = "";
    updateInput(input.id, "option_logos", nextLogos);
  };

  return (
    <div className="flex-1 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-700">{optionLabel}</span>
        <label
          className="flex items-center gap-2 text-sm text-gray-700"
          onMouseDown={preventDragHandling}
        >
          <Form.Item
            className="mb-0"
            valuePropName="checked"
            name={`show_logos_${input.id}`}
            noStyle
          >
            <Switch
              className="custom-switch custom-switch--compact"
              checked={showLogos}
              onChange={handleShowLogosChange}
            />
          </Form.Item>
          With logos
        </label>
      </div>

      {showLogos ? (
        <div className="space-y-3">
          <p className="mb-0 text-xs text-gray-500">
            A logo is required for every option. Drag to reorder.
          </p>
          <DragDropContext onDragEnd={handleOptionsDragEnd}>
            <Droppable
              droppableId={`choice-options-${input.id}`}
              type={`CHOICE_OPTIONS_${input.id}`}
              direction="vertical"
            >
              {(droppableProvided) => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className="space-y-3"
                >
                  {displayLines.map((line, index) => (
                    <Draggable
                      key={`${input.id}-choice-${index}`}
                      draggableId={`${input.id}-choice-${index}`}
                      index={index}
                    >
                      {(draggableProvided, snapshot) => {
                        const row = (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={lockVerticalDragStyle(
                              draggableProvided.draggableProps.style,
                            )}
                            className={`flex w-full items-center gap-2 ${
                              snapshot.isDragging
                                ? "z-[1000] border-gray-200 shadow-md"
                                : ""
                            }`}
                          >
                            <div
                              {...draggableProvided.dragHandleProps}
                              className="flex shrink-0 cursor-grab touch-none items-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                              aria-label="Drag to reorder"
                            >
                              <MenuOutlined />
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {logos[index] ? (
                                <>
                                  <img
                                    src={logos[index]}
                                    alt=""
                                    className="h-12 w-12 shrink-0 rounded-full border border-gray-200 bg-white object-cover shadow-sm"
                                  />
                                  <Upload {...logoUploadProps(index)}>
                                    <Button
                                      type="text"
                                      size="large"
                                      className="rounded-xl px-2"
                                      icon={<Pencil size={16} />}
                                      onMouseDown={preventDragHandling}
                                      loading={uploadingIndex === index}
                                      aria-label="Edit logo"
                                    />
                                  </Upload>
                                  <Button
                                    type="text"
                                    danger
                                    size="large"
                                    className="rounded-xl px-2"
                                    icon={<Trash2 size={16} />}
                                    onMouseDown={preventDragHandling}
                                    onClick={() => handleRemoveLogo(index)}
                                    aria-label="Delete logo"
                                  />
                                </>
                              ) : (
                                <Upload {...logoUploadProps(index)}>
                                  <Button
                                    size="large"
                                    danger
                                    className="rounded-xl"
                                    icon={<RxUpload className="text-lg" />}
                                    loading={uploadingIndex === index}
                                    onMouseDown={preventDragHandling}
                                    aria-label="Upload logo"
                                  />
                                </Upload>
                              )}
                            </div>
                            <Input
                              size="large"
                              value={line}
                              onChange={(e) =>
                                handleLineChange(index, e.target.value)
                              }
                              placeholder={`Option ${index + 1}`}
                              className="min-w-0 flex-1 rounded-xl"
                              onMouseDown={preventDragHandling}
                            />
                            {displayLines.length > 1 ? (
                              <Button
                                type="text"
                                danger
                                size="large"
                                className="shrink-0 rounded-xl"
                                icon={<Trash2 size={16} />}
                                onMouseDown={preventDragHandling}
                                onClick={() => handleRemoveLine(index)}
                                aria-label="Remove option"
                              />
                            ) : null}
                          </div>
                        );

                        return snapshot.isDragging
                          ? createPortal(row, document.body)
                          : row;
                      }}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <Button
            type="dashed"
            className="rounded-xl"
            onMouseDown={preventDragHandling}
            onClick={handleAddLine}
          >
            Add option
          </Button>
        </div>
      ) : (
        <Input.TextArea
          size="large"
          value={
            typeof input.options === "string"
              ? input.options
              : joinChoiceOptionEditorLines(input.options)
          }
          onChange={(e) => updateInput(input.id, "options", e.target.value)}
          placeholder={"One option per line, or comma-separated"}
          rows={4}
          className="rounded-xl"
          onMouseDown={preventDragHandling}
        />
      )}
    </div>
  );
};

export default ChoiceOptionsEditor;
